/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from 'react';
import { FaCog, FaPowerOff, FaRegStar, FaStar } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { setOpenModales } from '../../store/global';
import {
  canEditItemForItem,
  getId,
  getPicEntryAtIndex,
  resolvePicAsset,
} from '../../utils/item';
import { isEditable } from '../../utils/roles';
import { canUseMjForItem, isMjForItem } from '../../utils/auth';
import { InputLabel } from '../form/input.Label';
import { PicGlobal } from '../pic/pic.Global';
import { ItemSettings } from './item.Settings';
import { PicSettings } from '../pic/pic.Settings';
import { fetchImage } from '../../utils/img';
import { renderIconBySlug } from '../../content/icons';
import { getStorageJSON, setStorageJSON } from '../../utils/userStorage';

export const ItemHeader = props => {
  const { itemId, displayType } = props;

  const [specialPics, setSpecialPics] = useState({});
  const [pic, setPic] = useState({});
  const [favorites, setFavorites] = useState([]);
  const [hasLoadedFavorites, setHasLoadedFavorites] = useState(false);
  const [avatar, setAvatar] = useState(null);
  const [banner, setBanner] = useState(null);
  const [avatarIconSlug, setAvatarIconSlug] = useState(null);

  const item = useSelector(state => state.items[itemId]);
  const items = useSelector(state => state.items);
  const canMj = isMjForItem(item, items) || canUseMjForItem(item, items);
  const canEdit = canEditItemForItem(item, items);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!item?.pics) {
      setAvatar(null);
      setBanner(null);
      setAvatarIconSlug(null);
      setPic({});
      return;
    }

    const avatarEntry = getPicEntryAtIndex(item.pics, 0);
    const bannerEntry = getPicEntryAtIndex(item.pics, 1);
    const avatarAsset = resolvePicAsset(avatarEntry);
    const bannerAsset = resolvePicAsset(bannerEntry);

    if (avatarAsset.url) {
      fetchImage(avatarAsset.url, setAvatar);
    } else {
      setAvatar(null);
    }
    if (bannerAsset.url) {
      fetchImage(bannerAsset.url, setBanner);
    } else {
      setBanner(null);
    }

    setAvatarIconSlug(avatarAsset.iconSlug);
    if (avatarEntry) {
      setPic(avatarEntry);
    } else {
      setPic({});
    }
  }, [item?.pics]);

  useEffect(() => {
    setSpecialPics({ avatar, banner });
  }, [avatar, banner]);

  const avatarIconElement = useMemo(() => {
    if (!avatarIconSlug) {
      return null;
    }
    return renderIconBySlug(avatarIconSlug, {
      size: 140,
      color: 'var(--contrast)',
    });
  }, [avatarIconSlug]);

  useEffect(() => {
    const storedFavorites = getStorageJSON('favorites', []);
    if (Array.isArray(storedFavorites)) {
      setFavorites(storedFavorites);
    }
    setHasLoadedFavorites(true);
  }, []);

  const isFavorite = favorites.includes(itemId);

  useEffect(() => {
    if (!hasLoadedFavorites) {
      return;
    }
    setStorageJSON('favorites', favorites);
  }, [favorites, hasLoadedFavorites]);

  if (item.type === 'pic') {
    return (
      <div
        className='flex justify-center items-center flex-col'
        style={{
          position: 'relative',
          height: '100%',
        }}
      >
        <div
          className='absolute w-full h-full bg-main-d opacity-50'
          style={{
            ...(specialPics.avatar
              ? { background: `url(${specialPics.avatar}) center/cover` }
              : {}),
            filter: 'blur(10px)',
          }}
        />
        <div className='absolute w-full top-0 flex'>
          <InputLabel
            className={`w-full`}
            itemId={itemId}
          />
        </div>
        <div
          onDoubleClick={e => {
            e.stopPropagation();
            isEditable() &&
              canEdit &&
              dispatch(setOpenModales({ id: getId(item.pics[0]), open: true }));
          }}
        >
          <PicSettings
            picId={getId(item.pics[0])}
            itemId={itemId}
            pic={pic}
          />
          {avatarIconElement ? (
            <div
              className='flex items-center justify-center text-contrast text-opacity-80'
              style={{ position: 'relative', minHeight: 200 }}
            >
              {avatarIconElement}
            </div>
          ) : (
            specialPics.avatar && (
              <img
                src={specialPics.avatar}
                style={{ position: 'relative' }}
                alt=''
              />
            )
          )}
        </div>

        {canMj && (
          <>
            <ItemSettings itemId={itemId} />
            <div
              className='absolute z-20 top-1 right-1 transition cursor-pointer hover:opacity-50'
              onClick={() =>
                dispatch(
                  setOpenModales({ id: 'editCurrentItem' + itemId, open: true })
                )
              }
            >
              <FaCog />
            </div>
          </>
        )}
        {isEditable() === false && displayType === 'sidebar' && (
          <div
            className='absolute z-20 top-1 right-1 transition cursor-pointer hover:opacity-50'
            onClick={() => window.location.reload()}
          >
            <Link
              to={`../../item/${itemId}`}
              className='cursor'
            >
              <FaPowerOff />
            </Link>
          </div>
        )}
      </div>
    );
  } else {
    return (
      <div
        className={`relative w-full ${specialPics.banner ? 'mb-3' : 'mb-1'}`}
        style={{
          ...(specialPics.banner
            ? { background: `url(${specialPics.banner}) center/cover` }
            : {}),
          height: specialPics.banner || specialPics.avatar ? 100 : 30,
        }}
      >
        <div
          className='absolute z-20 top-1 left-1 transition cursor-pointer hover:opacity-50'
          onClick={() =>
            setFavorites(prev => {
              if (prev.includes(itemId)) {
                return prev.filter(fav => fav !== itemId);
              }
              return [...prev, itemId];
            })
          }
        >
          {isFavorite ? <FaStar /> : <FaRegStar />}
        </div>

        {canMj && (
          <>
            <ItemSettings itemId={itemId} />
            <div
              className='absolute z-20 top-1 right-1 transition cursor-pointer hover:opacity-50'
              onClick={() =>
                dispatch(
                  setOpenModales({ id: 'editCurrentItem' + itemId, open: true })
                )
              }
            >
              <FaCog />
            </div>
          </>
        )}
        {isEditable() === false && displayType === 'sidebar' && (
          <div
            className='absolute z-20 top-1 right-1 transition cursor-pointer hover:opacity-50'
            onClick={() => window.location.reload()}
          >
            <Link
              to={`../../item/${itemId}`}
              className='cursor'
            >
              <FaPowerOff />
            </Link>
          </div>
        )}

        {specialPics.banner && (
          <div className='absolute w-full h-full bg-gradient-to-t from-main to-transparent'></div>
        )}
        <div
          className={`absolute w-full flex justify-between items-center px-1 ${
            specialPics.banner
              ? 'top-3 -mt-2'
              : specialPics.avatar
              ? '-top-1'
              : 'top-0'
          }`}
        >
          {item?.pics?.[0] && (
            <div style={{ height: 150, width: 150 }}>
              <PicGlobal
                itemId={itemId}
                picId={getId(item.pics[0])}
                pic={pic}
                fieldInGrid={{ displayType: 'rond' }}
                canEdit={canEdit}
              />
            </div>
          )}
          <InputLabel
            className={`${specialPics.avatar ? 'w-3/4' : 'w-full'}`}
            itemId={itemId}
          />
        </div>
      </div>
    );
  }
};
